import React, { useEffect, useMemo, useState } from 'react';
import { Company, Lead, Task, User } from '../types';
import { api } from '../services/apiService';

interface TasksViewProps {
  user: User;
  company: Company;
}

const TasksView: React.FC<TasksViewProps> = ({ user, company }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  const [leadId, setLeadId] = useState('');
  const [clientCompanyName, setClientCompanyName] = useState('');
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [status, setStatus] = useState<'todo' | 'done'>('todo');

  const loadData = async () => {
    setLoading(true);
    try {
      const [leadData, taskData] = await Promise.all([
        api.getLeads({ companyId: company.id, userId: user.id, role: user.role }),
        api.getTasks({ companyId: company.id })
      ]);
      setLeads(leadData || []);
      setTasks(taskData || []);
      await api.checkUpcomingTaskReminders(user.id);
    } catch (e) {
      console.error('Erreur chargement taches', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [company.id, user.id, user.role]);

  const filteredTasks = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return tasks;
    return tasks.filter(t =>
      t.title.toLowerCase().includes(q) ||
      (t.comment || '').toLowerCase().includes(q) ||
      (t.clientCompanyName || '').toLowerCase().includes(q)
    );
  }, [tasks, searchTerm]);

  const [taskPage, setTaskPage] = useState(1);
  const taskPerPage = 25;
  const taskTotalPages = Math.ceil(filteredTasks.length / taskPerPage);
  const paginatedTasks = useMemo(() => {
    const start = (taskPage - 1) * taskPerPage;
    return filteredTasks.slice(start, start + taskPerPage);
  }, [filteredTasks, taskPage]);
  useEffect(() => { setTaskPage(1); }, [searchTerm]);

  const openCreateModal = () => {
    setEditingTask(null);
    setLeadId('');
    setClientCompanyName('');
    setTitle('');
    setComment('');
    setDueAt(new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16));
    setStatus('todo');
    setShowModal(true);
  };

  const openEditModal = (task: Task) => {
    setEditingTask(task);
    setLeadId(task.leadId || '');
    setClientCompanyName(task.clientCompanyName || '');
    setTitle(task.title);
    setComment(task.comment || '');
    setDueAt(new Date(task.dueAt).toISOString().slice(0, 16));
    setStatus(task.status);
    setShowModal(true);
  };

  const onSelectLead = (selectedLeadId: string) => {
    setLeadId(selectedLeadId);
    const lead = leads.find(l => String(l.id) === String(selectedLeadId));
    if (lead) setClientCompanyName(lead.clientCompanyName || '');
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !dueAt) return;
    setIsSubmitting(true);
    try {
      if (editingTask) {
        const updated = await api.updateTask(editingTask.id, {
          leadId: leadId || undefined,
          clientCompanyName: clientCompanyName || undefined,
          title: title.trim(),
          comment: comment.trim(),
          dueAt: new Date(dueAt).toISOString(),
          status
        });
        setTasks(prev => prev.map(t => t.id === updated.id ? updated : t));
      } else {
        const created = await api.createTask({
          companyId: company.id,
          leadId: leadId || undefined,
          clientCompanyName: clientCompanyName || undefined,
          title: title.trim(),
          comment: comment.trim(),
          dueAt: new Date(dueAt).toISOString()
        }, { id: user.id, name: user.name });
        setTasks(prev => [created, ...prev]);
      }
      await api.checkUpcomingTaskReminders(user.id);
      setShowModal(false);
    } catch (error) {
      console.error('Erreur sauvegarde tache', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleDone = async (e: React.MouseEvent, task: Task) => {
    e.stopPropagation();
    try {
      const updated = await api.updateTask(task.id, { status: task.status === 'done' ? 'todo' : 'done' });
      setTasks(prev => prev.map(t => t.id === updated.id ? updated : t));
    } catch (err) {
      console.error('Erreur validation tache', err);
    }
  };

  const [confirmDeleteTaskId, setConfirmDeleteTaskId] = useState<string | null>(null);
  const handleDeleteTask = async (e: React.MouseEvent, taskId: string) => {
    e.stopPropagation();
    try {
      await api.deleteTask(taskId);
      setTasks(prev => prev.filter(t => t.id !== taskId));
      setConfirmDeleteTaskId(null);
    } catch (err) {
      console.error('Erreur suppression tache', err);
    }
  };

  const isOverdue = (task: Task) => task.status !== 'done' && new Date(task.dueAt).getTime() < Date.now();
  const isDueSoon = (task: Task) => {
    if (task.status === 'done') return false;
    const diff = new Date(task.dueAt).getTime() - Date.now();
    return diff >= 0 && diff <= 2 * 60 * 60 * 1000;
  };

  return (
    <div className="orion-dashboard space-y-4 animate-in fade-in duration-500 flex-1 min-h-0 flex flex-col">

      {/* Toolbar */}
      <div className="orion-toolbar flex flex-col md:flex-row justify-between items-center gap-3 p-1.5 rounded-xl border flex-shrink-0">
        <div className="relative w-full md:w-80">
          <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#546a96]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Rechercher une tâche..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-[rgba(100,140,210,0.06)] border border-[rgba(100,140,210,0.15)] rounded-lg text-[13px] font-bold text-[#c8daf5] placeholder-[#546a96] outline-none focus:border-[rgba(17,197,214,0.4)] transition-all"
          />
        </div>
        <button type="button" onClick={openCreateModal} className="orion-primary-btn text-white px-4 py-2.5 rounded-lg text-[11px] font-black flex items-center gap-2">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" /></svg>
          Nouvelle tâche
        </button>
      </div>

      {/* Table */}
      <div className="orion-table-shell flex-1 rounded-xl border overflow-hidden flex flex-col">
        <div className="overflow-x-auto custom-scrollbar flex-1">
          <table className="orion-resp-table w-full text-left border-collapse">
            <thead className="orion-table-head sticky top-0 z-10">
              <tr className="text-[10px] font-black uppercase tracking-widest">
                <th className="px-5 py-4 w-28">Statut</th>
                <th className="px-5 py-4">Tâche</th>
                <th className="px-5 py-4 resp-hide">Client</th>
                <th className="px-5 py-4 resp-hide">Échéance</th>
                <th className="px-5 py-4 text-right w-28">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="text-center py-12 text-[#546a96] text-xs font-bold animate-pulse">Chargement...</td></tr>
              )}
              {!loading && paginatedTasks.map(task => {
                const overdue = isOverdue(task);
                const soon = isDueSoon(task);
                return (
                  <tr
                    key={task.id}
                    onClick={() => openEditModal(task)}
                    className="orion-table-row-hover group transition-colors cursor-pointer border-b border-[rgba(100,140,210,0.06)]"
                  >
                    {/* Statut badge */}
                    <td className="px-5 py-3.5">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wide border ${
                        task.status === 'done'
                          ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                          : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${task.status === 'done' ? 'bg-emerald-400' : 'bg-amber-400'}`}></span>
                        {task.status === 'done' ? 'Validée' : 'À faire'}
                      </span>
                    </td>

                    {/* Titre + commentaire */}
                    <td className="px-5 py-3.5">
                      <div className={`text-[13px] font-bold text-slate-900 ${task.status === 'done' ? 'line-through opacity-50' : ''}`}>{task.title}</div>
                      {task.comment && <div className="text-[11px] text-slate-500 mt-0.5 truncate max-w-xs">{task.comment}</div>}
                    </td>

                    {/* Client */}
                    <td className="resp-hide px-5 py-3.5 text-[12px] font-bold text-slate-700">{task.clientCompanyName || <span className="text-slate-400">—</span>}</td>

                    {/* Échéance */}
                    <td className="resp-hide px-5 py-3.5">
                      <span className={`text-[11px] font-bold ${overdue ? 'text-rose-400' : soon ? 'text-amber-400' : 'text-slate-500'}`}>
                        {overdue && <span className="mr-1">⚠</span>}
                        {new Date(task.dueAt).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </td>

                    {/* Actions (icônes) */}
                    <td className="px-5 py-3.5" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {/* Valider / Réouvrir */}
                        <button
                          type="button"
                          title={task.status === 'done' ? 'Réouvrir' : 'Marquer validée'}
                          onClick={(e) => toggleDone(e, task)}
                          className={`p-2 rounded-lg transition-all ${task.status === 'done' ? 'text-[#546a96] hover:text-amber-400 hover:bg-amber-500/10' : 'text-[#546a96] hover:text-emerald-400 hover:bg-emerald-500/10'}`}
                        >
                          {task.status === 'done' ? (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                          )}
                        </button>

                        {/* Modifier */}
                        <button
                          type="button"
                          title="Modifier"
                          onClick={(e) => { e.stopPropagation(); openEditModal(task); }}
                          className="p-2 rounded-lg text-[#546a96] hover:text-[#11c5d6] hover:bg-[rgba(17,197,214,0.1)] transition-all"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        </button>

                        {/* Supprimer */}
                        {confirmDeleteTaskId === task.id ? (
                          <div className="flex items-center gap-1 animate-in fade-in slide-in-from-right-2">
                            <span className="text-[9px] font-black text-rose-400 uppercase">Sûr ?</span>
                            <button type="button" onClick={(e) => handleDeleteTask(e, task.id)} className="px-2 py-1 rounded-md text-[10px] font-black bg-rose-500 text-white hover:bg-rose-600">Oui</button>
                            <button type="button" onClick={(e) => { e.stopPropagation(); setConfirmDeleteTaskId(null); }} className="px-2 py-1 rounded-md text-[10px] font-black bg-[rgba(100,140,210,0.1)] text-[#7f93bc] hover:bg-[rgba(100,140,210,0.2)]">Non</button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            title="Supprimer"
                            onClick={(e) => { e.stopPropagation(); setConfirmDeleteTaskId(task.id); }}
                            className="p-2 rounded-lg text-[#546a96] hover:text-rose-400 hover:bg-rose-500/10 transition-all"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {!loading && filteredTasks.length === 0 && (
            <div className="text-center py-16 text-[#546a96] text-xs font-bold">Aucune tâche trouvée.</div>
          )}
        </div>

        {/* Pagination */}
        {taskTotalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-[rgba(100,140,210,0.1)]">
            <span className="text-[11px] font-bold text-[#546a96]">{((taskPage - 1) * taskPerPage) + 1}–{Math.min(taskPage * taskPerPage, filteredTasks.length)} sur {filteredTasks.length}</span>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => setTaskPage(1)} disabled={taskPage === 1} className="px-2 py-1 rounded-lg text-[11px] font-bold text-[#546a96] hover:bg-[rgba(100,140,210,0.1)] disabled:opacity-30">{'«'}</button>
              <button type="button" onClick={() => setTaskPage(p => Math.max(1, p - 1))} disabled={taskPage === 1} className="px-2 py-1 rounded-lg text-[11px] font-bold text-[#546a96] hover:bg-[rgba(100,140,210,0.1)] disabled:opacity-30">{'‹'}</button>
              {Array.from({ length: Math.min(5, taskTotalPages) }, (_, i) => {
                let page: number;
                if (taskTotalPages <= 5) page = i + 1;
                else if (taskPage <= 3) page = i + 1;
                else if (taskPage >= taskTotalPages - 2) page = taskTotalPages - 4 + i;
                else page = taskPage - 2 + i;
                return (
                  <button type="button" key={page} onClick={() => setTaskPage(page)}
                    className={`w-7 h-7 rounded-lg text-[11px] font-black transition-all ${taskPage === page ? 'bg-[rgba(17,197,214,0.2)] text-[#11c5d6]' : 'text-[#546a96] hover:bg-[rgba(100,140,210,0.1)]'}`}>
                    {page}
                  </button>
                );
              })}
              <button type="button" onClick={() => setTaskPage(p => Math.min(taskTotalPages, p + 1))} disabled={taskPage === taskTotalPages} className="px-2 py-1 rounded-lg text-[11px] font-bold text-[#546a96] hover:bg-[rgba(100,140,210,0.1)] disabled:opacity-30">{'›'}</button>
              <button type="button" onClick={() => setTaskPage(taskTotalPages)} disabled={taskPage === taskTotalPages} className="px-2 py-1 rounded-lg text-[11px] font-bold text-[#546a96] hover:bg-[rgba(100,140,210,0.1)] disabled:opacity-30">{'»'}</button>
            </div>
          </div>
        )}
      </div>

      {/* Modal création / édition */}
      {showModal && (
        <div className="orion-modal-backdrop fixed inset-0 z-[9999] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
          <div className="orion-modal-panel w-full max-w-2xl rounded-2xl shadow-2xl p-4 sm:p-8 border animate-in zoom-in-95">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-black text-[#e0eaff]">{editingTask ? 'Modifier la tâche' : 'Nouvelle tâche'}</h3>
              <button type="button" title="Fermer" onClick={() => setShowModal(false)} className="p-2 rounded-lg text-[#546a96] hover:text-rose-400 hover:bg-rose-500/10 transition-all">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-black text-[#546a96] uppercase tracking-widest mb-1.5 block">Prospect lié</label>
                  <select value={leadId} onChange={e => onSelectLead(e.target.value)} className="orion-modal-input w-full rounded-xl px-4 py-3 text-[13px] font-bold outline-none focus:ring-2 focus:ring-[rgba(17,197,214,0.4)]">
                    <option value="">Aucun</option>
                    {leads.map(lead => (
                      <option key={lead.id} value={lead.id}>{(lead.clientCompanyName || 'Client')} — {lead.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-black text-[#546a96] uppercase tracking-widest mb-1.5 block">Nom client</label>
                  <input type="text" value={clientCompanyName} onChange={e => setClientCompanyName(e.target.value)} className="orion-modal-input w-full rounded-xl px-4 py-3 text-[13px] font-bold outline-none focus:ring-2 focus:ring-[rgba(17,197,214,0.4)]" />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-black text-[#546a96] uppercase tracking-widest mb-1.5 block">Titre *</label>
                <input type="text" required value={title} onChange={e => setTitle(e.target.value)} placeholder="Titre de la tâche" className="orion-modal-input w-full rounded-xl px-4 py-3 text-[13px] font-bold outline-none focus:ring-2 focus:ring-[rgba(17,197,214,0.4)]" />
              </div>
              <div>
                <label className="text-[10px] font-black text-[#546a96] uppercase tracking-widest mb-1.5 block">Commentaire</label>
                <textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Notes, détails..." className="orion-modal-input w-full rounded-xl px-4 py-3 text-[13px] font-medium outline-none focus:ring-2 focus:ring-[rgba(17,197,214,0.4)] h-24 resize-none" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-black text-[#546a96] uppercase tracking-widest mb-1.5 block">Échéance *</label>
                  <input type="datetime-local" required value={dueAt} onChange={e => setDueAt(e.target.value)} className="orion-modal-input w-full rounded-xl px-4 py-3 text-[13px] font-bold outline-none focus:ring-2 focus:ring-[rgba(17,197,214,0.4)]" />
                </div>
                {editingTask && (
                  <div>
                    <label className="text-[10px] font-black text-[#546a96] uppercase tracking-widest mb-1.5 block">Statut</label>
                    <select value={status} onChange={e => setStatus(e.target.value as 'todo' | 'done')} className="orion-modal-input w-full rounded-xl px-4 py-3 text-[13px] font-bold outline-none focus:ring-2 focus:ring-[rgba(17,197,214,0.4)]">
                      <option value="todo">À faire</option>
                      <option value="done">Validée</option>
                    </select>
                  </div>
                )}
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-3 rounded-xl text-[12px] font-bold text-[#546a96] hover:text-[#7f93bc] bg-[rgba(100,140,210,0.06)] hover:bg-[rgba(100,140,210,0.1)] transition-all">Annuler</button>
                <button type="submit" disabled={isSubmitting} className="orion-modal-primary-btn flex-1 py-3 rounded-xl text-[12px] font-bold disabled:opacity-50">
                  {isSubmitting ? 'Sauvegarde...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TasksView;
